import React, { useState, useEffect, useCallback } from 'react';
import { useToast } from '../../../components/Toast';
import { getImageUrl as resolveMediaSrc } from '../../../utils/mediaUtils';
import { 
  Shield, 
  Heart, 
  Lightbulb, 
  Cross, 
  Feather, 
  Save, 
  Upload, 
  Plus, 
  Trash2
} from 'lucide-react';
import './FormsUnified.css';

const MAX_IMAGE_SIZE = 5 * 1024 * 1024;
const ALLOWED_TYPES = ['image/jpeg', 'image/png', 'image/webp'];

const DEFAULT_HOUSES_DATA = {
  'house-house-of-courage': {
    name: 'House of Courage',
    motto: 'Courage Unlocks Dreams',
    color: '#dc2626',
    colorLight: '#fee2e2',
    icon: 'fa-shield',
    image: '/assets/news/news1.jpg',
    description: 'House of Courage embodies the spirit of bravery, determination, and fearless leadership. Our members are bold explorers who embrace challenges with unwavering confidence and inspire others through their courageous actions.',
    values: [
      'Brave leadership',
      'Fearless determination',
      'Resilience and strength',
      'Bold innovation',
      'Inspiring others'
    ],
    achievements: [
      'Leadership Excellence Award 2024',
      'Courage in Action Recognition',
      'Sports Championship Winners',
      'Innovation Challenge Champions'
    ],
    members: 'Approximately 180 students across all levels',
    houseLeader: 'Mr. James Wilson',
    activities: [
      'Leadership development programs',
      'Courage workshops and seminars',
      'Sports training and competitions',
      'Motivational speaking events',
      'Challenge and adventure activities'
    ],
    highlights: 'House of Courage is known for fostering bold leaders who take risks, embrace challenges, and inspire their peers. Our house culture celebrates bravery, determination, and the courage to make a difference.'
  },
  'house-house-of-love': {
    name: 'House of Love',
    motto: 'Love Guides Us Forward',
    color: '#ec4899',
    colorLight: '#fce7f3',
    icon: 'fa-heart',
    image: '/assets/news/news3.jpg',
    description: 'House of Love stands for compassion, kindness, and genuine care for one another. Our members create a nurturing environment where everyone feels valued, supported, and celebrated for who they are.',
    values: [
      'Compassion and empathy',
      'Genuine care',
      'Inclusivity and belonging',
      'Emotional support',
      'Community love'
    ],
    achievements: [
      'Community Service Excellence 2024',
      'Kindness Award',
      'Peer Support Recognition',
      'Inclusion and Diversity Prize'
    ],
    members: 'Approximately 175 students across all levels',
    houseLeader: 'Ms. Sarah Chen',
    activities: [
      'Community service projects',
      'Peer mentoring programs',
      'Wellness and support circles',
      'Inclusive social events',
      'Charity and fundraising initiatives'
    ],
    highlights: 'House of Love creates a warm, welcoming community where love and compassion are the foundation. Members actively support each other and extend genuine care to the broader school community.'
  },
  'house-house-of-hope': {
    name: 'House of Hope',
    motto: 'Hope Lights the Way',
    color: '#eab308',
    colorLight: '#fef3c7',
    icon: 'fa-lightbulb',
    image: '/assets/news/news4.jpg',
    description: 'House of Hope inspires optimism, positivity, and belief in a brighter future. Our members are visionaries who see possibilities, encourage others, and work together to create meaningful change and lasting impact.',
    values: [
      'Optimism and positivity',
      'Vision for the future',
      'Inspiring hope',
      'Collaborative growth',
      'Meaningful impact'
    ],
    achievements: [
      'Vision and Innovation Award 2024',
      'Positive Impact Recognition',
      'Future Leaders Prize',
      'Inspiration and Motivation Award'
    ],
    members: 'Approximately 180 students across all levels',
    houseLeader: 'Mr. David Martinez',
    activities: [
      'Vision and goal-setting workshops',
      'Inspirational speaker series',
      'Future leaders development',
      'Positive change initiatives',
      'Hope and wellness programs'
    ],
    highlights: 'House of Hope is the beacon of optimism and possibility. Our members inspire others through their positive outlook, visionary thinking, and commitment to creating a better tomorrow for everyone.'
  },
  'house-house-of-faith': {
    name: 'House of Faith',
    motto: 'Faith Guides Our Path',
    color: '#f97316',
    colorLight: '#ffedd5',
    icon: 'fa-cross',
    image: '/assets/news/news6.jpg',
    description: 'House of Faith is built on trust, belief, and unwavering commitment to our values. Our members demonstrate integrity, loyalty, and steadfast dedication to supporting one another and upholding our shared principles.',
    values: [
      'Trust and integrity',
      'Loyalty and commitment',
      'Steadfast values',
      'Belief in each other',
      'Principled action'
    ],
    achievements: [
      'Character Excellence Award 2024',
      'Integrity and Trust Recognition',
      'Loyalty Award',
      'Values Leadership Prize'
    ],
    members: 'Approximately 170 students across all levels',
    houseLeader: 'Ms. Emily Rodriguez',
    activities: [
      'Character development programs',
      'Values and ethics discussions',
      'Trust-building activities',
      'Integrity workshops',
      'Community commitment projects'
    ],
    highlights: 'House of Faith cultivates a culture of trust, integrity, and unwavering commitment. Members are encouraged to believe in themselves and each other, and to act with principled conviction in all they do.'
  },
  'house-house-of-peace': {
    name: 'House of Peace',
    motto: 'Peace Leads Us Forward',
    color: '#22c55e',
    colorLight: '#dcfce7',
    icon: 'fa-dove',
    image: '/assets/news/news7.jpg',
    description: 'House of Peace promotes harmony, understanding, and peaceful collaboration. Our members are peacemakers who bridge differences, foster unity, and create an environment where everyone can thrive in tranquility and mutual respect.',
    values: [
      'Harmony and balance',
      'Understanding and empathy',
      'Peaceful collaboration',
      'Unity and respect',
      'Conflict resolution'
    ],
    achievements: [
      'Peace and Harmony Award 2024',
      'Conflict Resolution Excellence',
      'Unity and Understanding Prize',
      'Community Harmony Recognition'
    ],
    members: 'Approximately 175 students across all levels',
    houseLeader: 'Mr. Christopher Lee',
    activities: [
      'Conflict resolution workshops',
      'Meditation and mindfulness sessions',
      'Peacemaking initiatives',
      'Unity and understanding events',
      'Harmony and wellness programs'
    ],
    highlights: 'House of Peace is the sanctuary of harmony and understanding. Our members work tirelessly to bridge differences, foster unity, and create a peaceful environment where all can flourish together.'
  }
};

const HOUSES_LIST = [
  { id: 'house-house-of-courage', name: 'House of Courage', icon: Shield, color: '#dc2626' },
  { id: 'house-house-of-love', name: 'House of Love', icon: Heart, color: '#ec4899' },
  { id: 'house-house-of-hope', name: 'House of Hope', icon: Lightbulb, color: '#eab308' },
  { id: 'house-house-of-faith', name: 'House of Faith', icon: Cross, color: '#f97316' },
  { id: 'house-house-of-peace', name: 'House of Peace', icon: Feather, color: '#22c55e' }
];

export default function StudentLifeHouseDetailsForm({ apiBase }) {
  const { toast, ToastContainer } = useToast();

  const [selectedHouseId, setSelectedHouseId] = useState('house-house-of-courage');
  const [housesData, setHousesData] = useState(DEFAULT_HOUSES_DATA);
  const [originalData, setOriginalData] = useState(DEFAULT_HOUSES_DATA);
  const [pendingFields, setPendingFields] = useState({});
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState({});
  const [sectionId, setSectionId] = useState(null);
  const [mediaMap, setMediaMap] = useState({});
  const [imageUploading, setImageUploading] = useState(false);

  const getAuthHeaders = useCallback(() => {
    let token = localStorage.getItem('token') || 
                localStorage.getItem('ph_auth_token') || 
                localStorage.getItem('auth_token') || 
                sessionStorage.getItem('token');
    if (!token) {
      try {
        const u = JSON.parse(localStorage.getItem('user') || '{}');
        if (u?.token) token = u.token;
      } catch (e) {}
    }
    return {
      'Content-Type': 'application/json',
      ...(token ? { 'Authorization': `Bearer ${token}` } : {})
    };
  }, []);

  const fetchMediaMap = useCallback(async () => {
    try {
      const res = await fetch(`${apiBase}/homepage/cms/media?type=image&_t=${Date.now()}`, {
        credentials: 'include',
        headers: getAuthHeaders()
      });
      if (res.ok) {
        const json = await res.json();
        const items = json.data?.items || json.data || [];
        if (Array.isArray(items)) {
          const map = {};
          items.forEach(m => {
            if (m.id && m.file_path) {
              map[m.id] = m.file_path;
              map[String(m.id)] = m.file_path;
            }
          });
          setMediaMap(map);
        }
      }
    } catch (e) {
      console.warn('[StudentLifeHouseDetailsForm] Media fetch error:', e);
    }
  }, [apiBase, getAuthHeaders]);

  const resolveImage = useCallback((imgSrc) => {
    if (!imgSrc) return null;
    const str = String(imgSrc).trim();
    if (!str) return null;
    if (mediaMap[str]) return mediaMap[str];
    const num = Number(str);
    if (!isNaN(num) && mediaMap[num]) return mediaMap[num];
    const resolved = resolveMediaSrc(str, mediaMap);
    if (resolved) {
      if (resolved.startsWith('http://') || resolved.startsWith('https://') || resolved.startsWith('blob:') || resolved.startsWith('data:')) {
        return resolved;
      }
      const origin = (apiBase || '').replace(/\/api\/?$/, '') || 'https://permataharapansecondary.sch.id';
      return `${origin}${resolved.startsWith('/') ? '' : '/'}${resolved}`;
    }
    if (str.startsWith('http://') || str.startsWith('https://') || str.startsWith('blob:') || str.startsWith('data:')) {
      return str;
    }
    if (str.startsWith('/')) {
      const origin = (apiBase || '').replace(/\/api\/?$/, '') || 'https://permataharapansecondary.sch.id';
      return `${origin}${str}`;
    }
    return str;
  }, [mediaMap, apiBase]);

  const loadSection = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${apiBase}/homepage/cms/sections?page=student-life`, {
        credentials: 'include',
        headers: getAuthHeaders()
      });

      let foundSection = null;
      if (res.ok) {
        const json = await res.json();
        const items = Array.isArray(json.data) ? json.data : Object.values(json.data || {});
        foundSection = items.find(s => s.section_type === 'house-details' || s.section_type === 'student-life-house-details');
      }

      if (foundSection) {
        setSectionId(foundSection.id);
        let rawContent = foundSection.content_published || foundSection.content;
        if (typeof rawContent === 'string') {
          try {
            rawContent = JSON.parse(rawContent);
          } catch (e) {
            rawContent = {};
          }
        }

        const dataEn = rawContent?.en || rawContent || {};
        const merged = { ...DEFAULT_HOUSES_DATA };
        Object.keys(DEFAULT_HOUSES_DATA).forEach(key => {
          if (dataEn[key]) {
            merged[key] = {
              ...DEFAULT_HOUSES_DATA[key],
              ...dataEn[key]
            };
          }
        });

        setHousesData(merged);
        setOriginalData(merged);

        try {
          const pendRes = await fetch(`${apiBase}/cms/section/${foundSection.id}/with-pending`, {
            credentials: 'include',
            headers: getAuthHeaders()
          });
          if (pendRes.ok) {
            const pendJson = await pendRes.json();
            if (pendJson.success && Array.isArray(pendJson.data?.pending_changes)) {
              const pendMap = {};
              pendJson.data.pending_changes.forEach(c => {
                if (c.status === 'pending') {
                  pendMap[c.field_id] = true;
                }
              });
              setPendingFields(pendMap);
            }
          }
        } catch (pe) {
          console.warn('[StudentLifeHouseDetailsForm] Pending check error:', pe);
        }
      }
    } catch (err) {
      console.error('[StudentLifeHouseDetailsForm] Load error:', err);
      toast.error('Gagal memuat data House Detail');
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [apiBase, getAuthHeaders]);

  useEffect(() => {
    fetchMediaMap();
    loadSection();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [apiBase]);

  const currentHouse = housesData[selectedHouseId] || DEFAULT_HOUSES_DATA[selectedHouseId];

  const handleHouseFieldChange = (field, value) => {
    setHousesData(prev => ({
      ...prev,
      [selectedHouseId]: {
        ...prev[selectedHouseId],
        [field]: value
      }
    }));
  };

  const handleListChange = (field, index, value) => {
    setHousesData(prev => {
      const updatedList = [...(prev[selectedHouseId][field] || [])];
      updatedList[index] = value;
      return {
        ...prev,
        [selectedHouseId]: {
          ...prev[selectedHouseId],
          [field]: updatedList
        }
      };
    });
  };

  const handleAddListItem = (field, placeholderText) => {
    setHousesData(prev => ({
      ...prev,
      [selectedHouseId]: {
        ...prev[selectedHouseId],
        [field]: [...(prev[selectedHouseId][field] || []), placeholderText]
      }
    }));
  };

  const handleRemoveListItem = (field, index) => {
    setHousesData(prev => ({
      ...prev,
      [selectedHouseId]: {
        ...prev[selectedHouseId],
        [field]: (prev[selectedHouseId][field] || []).filter((_, i) => i !== index)
      }
    }));
  };

  const submitHouseSection = async () => {
    if (!sectionId) {
      toast.error('Section ID belum terdaftar di database.');
      return;
    }

    const fieldKey = selectedHouseId;
    setSubmitting(prev => ({ ...prev, [fieldKey]: true }));
    try {
      const res = await fetch(`${apiBase}/cms/field-change/submit`, {
        method: 'POST',
        headers: getAuthHeaders(),
        credentials: 'include',
        body: JSON.stringify({
          section_id: sectionId,
          field_id: fieldKey,
          field_name: `House Detail: ${currentHouse.name}`,
          old_value: originalData[fieldKey],
          new_value: housesData[fieldKey],
          locale: 'en'
        })
      });

      const json = await res.json();
      if (json.success || json.ok) {
        toast.success(`Data detail ${currentHouse.name} berhasil diajukan untuk review`);
        setPendingFields(prev => ({ ...prev, [fieldKey]: true }));
      } else {
        toast.error(json.error || json.message || 'Gagal mengajukan perubahan');
      }
    } catch (e) {
      console.error('[StudentLifeHouseDetailsForm] Submit error:', e);
      toast.error('Terjadi kesalahan: ' + e.message);
    } finally {
      setSubmitting(prev => ({ ...prev, [fieldKey]: false }));
    }
  };

  const handleImageFileChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > MAX_IMAGE_SIZE) {
      toast.error('Ukuran gambar maksimal 5MB');
      return;
    }
    if (!ALLOWED_TYPES.includes(file.type)) {
      toast.error('Format gambar harus JPG, PNG, atau WEBP');
      return;
    }

    setImageUploading(true);
    try {
      const data = new FormData();
      data.append('file', file);
      data.append('type', 'image');

      let token = localStorage.getItem('token') || localStorage.getItem('ph_auth_token') || sessionStorage.getItem('token');
      const res = await fetch(`${apiBase}/homepage/cms/media`, {
        method: 'POST',
        credentials: 'include',
        headers: token ? { 'Authorization': `Bearer ${token}` } : {},
        body: data
      });

      const json = await res.json();
      if (json.ok && json.data) {
        const url = json.data.url || json.data.file_path;
        handleHouseFieldChange('image', url);
        toast.success('Foto berhasil diupload');
      } else {
        toast.error(json.error || 'Gagal upload foto');
      }
    } catch (e) {
      console.error('[StudentLifeHouseDetailsForm] Image upload error:', e);
      toast.error('Gagal upload: ' + e.message);
    } finally {
      setImageUploading(false);
    }
  };

  if (loading) {
    return (
      <div className="unified-loading-container">
        <div className="unified-spinner" />
        <p>Memuat data House Detail Pages...</p>
      </div>
    );
  }

  return (
    <div className="unified-form-wrapper">
      <ToastContainer />

      {/* Top Banner */}
      <div className="unified-header-card">
        <div className="header-info">
          <h2>House System Detail Pages</h2>
          <p>Kelola konten lengkap detail page untuk masing-masing 5 House: motto, kepemimpinan, nilai inti, prestasi, dan kegiatan.</p>
        </div>
        <div className="header-actions">
          <button
            type="button"
            className="unified-btn-primary"
            onClick={submitHouseSection}
            disabled={submitting[selectedHouseId]}
          >
            <Save size={16} />
            <span>{submitting[selectedHouseId] ? 'Menyimpan...' : `Simpan ${currentHouse.name}`}</span>
          </button>
        </div>
      </div>

      {/* House Selector Pills */}
      <div className="unified-tab-bar sub-tabs-bar">
        {HOUSES_LIST.map(h => {
          const Icon = h.icon;
          const isActive = h.id === selectedHouseId;
          return (
            <button
              key={h.id}
              type="button"
              className={`tab-pill-btn ${isActive ? 'active' : ''}`}
              onClick={() => setSelectedHouseId(h.id)}
            >
              <Icon size={16} className="w-4 h-4 mr-2" style={{ color: h.color }} />
              <span>{h.name}</span>
            </button>
          );
        })}
      </div>

      {/* Selected House Content Form */}
      <div className="unified-section-card">
        <div className="card-header" style={{ borderLeft: `6px solid ${currentHouse.color}` }}>
          <div className="title-with-icon">
            <Shield size={20} style={{ color: currentHouse.color }} />
            <h3>{currentHouse.name} - Profile & Header</h3>
          </div>
          {pendingFields[selectedHouseId] && (
            <span className="field-pending-tag">Pending Review</span>
          )}
        </div>

        <div className="card-body form-grid-2">
          <div className="form-group">
            <label>House Name</label>
            <input
              type="text"
              value={currentHouse.name}
              onChange={e => handleHouseFieldChange('name', e.target.value)}
            />
          </div>

          <div className="form-group">
            <label>Motto</label>
            <input
              type="text"
              value={currentHouse.motto}
              onChange={e => handleHouseFieldChange('motto', e.target.value)}
              placeholder="Courage Unlocks Dreams"
            />
          </div>

          <div className="form-group">
            <label>Theme Color (Hex)</label>
            <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
              <input
                type="color"
                value={currentHouse.color}
                onChange={e => handleHouseFieldChange('color', e.target.value)}
                style={{ width: '45px', height: '38px', padding: 0, cursor: 'pointer' }}
              />
              <input
                type="text"
                value={currentHouse.color}
                onChange={e => handleHouseFieldChange('color', e.target.value)}
                placeholder="#dc2626"
              />
            </div>
          </div>

          <div className="form-group">
            <label>Light Background Color (Hex)</label>
            <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
              <input
                type="color"
                value={currentHouse.colorLight || '#fef2f2'}
                onChange={e => handleHouseFieldChange('colorLight', e.target.value)}
                style={{ width: '45px', height: '38px', padding: 0, cursor: 'pointer' }}
              />
              <input
                type="text"
                value={currentHouse.colorLight || '#fef2f2'}
                onChange={e => handleHouseFieldChange('colorLight', e.target.value)}
                placeholder="#fee2e2"
              />
            </div>
          </div>

          <div className="form-group">
            <label>House Leader</label>
            <input
              type="text"
              value={currentHouse.houseLeader}
              onChange={e => handleHouseFieldChange('houseLeader', e.target.value)}
              placeholder="Mr. James Wilson"
            />
          </div>

          <div className="form-group">
            <label>Members Count / Description</label>
            <input
              type="text"
              value={currentHouse.members}
              onChange={e => handleHouseFieldChange('members', e.target.value)}
              placeholder="Approximately 180 students across all levels"
            />
          </div>

          <div className="form-group full-width">
            <label>Header / Showcase Image</label>
            <div className="image-field-block">
              {currentHouse.image ? (
                <div className="image-preview-box" style={{ maxWidth: '320px', height: '180px', marginBottom: '8px' }}>
                  <img src={resolveImage(currentHouse.image)} alt={currentHouse.name} style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: '8px' }} />
                </div>
              ) : (
                <div className="no-image-placeholder">Belum ada foto</div>
              )}
              <div style={{ display: 'flex', gap: '8px' }}>
                <label className="btn-upload-unified">
                  <Upload size={14} />
                  <span>{imageUploading ? 'Uploading...' : 'Ganti Foto'}</span>
                  <input type="file" accept="image/*" onChange={handleImageFileChange} disabled={imageUploading} style={{ display: 'none' }} />
                </label>
              </div>
            </div>
          </div>

          <div className="form-group full-width">
            <label>Overview Narrative / Description</label>
            <textarea
              rows={3}
              value={currentHouse.description}
              onChange={e => handleHouseFieldChange('description', e.target.value)}
              placeholder="Penjelasan menyeluruh profil house..."
            />
          </div>

          <div className="form-group full-width">
            <label>Highlight Quote / Key Message</label>
            <textarea
              rows={2}
              value={currentHouse.highlights}
              onChange={e => handleHouseFieldChange('highlights', e.target.value)}
              placeholder="Pesan kunci sorotan keunikan house..."
            />
          </div>
        </div>

        {/* Values, Achievements, and Activities */}
        <div className="sub-section-divider">
          <h4>Core Values, Achievements, & Activities</h4>
        </div>

        <div className="card-body form-grid-3">
          {/* Values List */}
          <div className="item-sub-card">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
              <span className="item-counter">Core Values (Nilai Inti)</span>
              <button type="button" className="btn-field-save" onClick={() => handleAddListItem('values', 'New Core Value')}>
                <Plus size={12} /> Tambah
              </button>
            </div>
            {(currentHouse.values || []).map((val, idx) => (
              <div key={idx} className="input-with-action" style={{ marginBottom: '6px' }}>
                <input
                  type="text"
                  value={val}
                  onChange={e => handleListChange('values', idx, e.target.value)}
                />
                <button type="button" className="btn-item-delete" onClick={() => handleRemoveListItem('values', idx)}>
                  <Trash2 size={12} />
                </button>
              </div>
            ))}
          </div>

          {/* Achievements List */}
          <div className="item-sub-card">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
              <span className="item-counter">Key Achievements (Prestasi)</span>
              <button type="button" className="btn-field-save" onClick={() => handleAddListItem('achievements', 'New Achievement')}>
                <Plus size={12} /> Tambah
              </button>
            </div>
            {(currentHouse.achievements || []).map((ach, idx) => (
              <div key={idx} className="input-with-action" style={{ marginBottom: '6px' }}>
                <input
                  type="text"
                  value={ach}
                  onChange={e => handleListChange('achievements', idx, e.target.value)}
                />
                <button type="button" className="btn-item-delete" onClick={() => handleRemoveListItem('achievements', idx)}>
                  <Trash2 size={12} />
                </button>
              </div>
            ))}
          </div>

          {/* Activities List */}
          <div className="item-sub-card">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
              <span className="item-counter">Signature Activities</span>
              <button type="button" className="btn-field-save" onClick={() => handleAddListItem('activities', 'New Activity')}>
                <Plus size={12} /> Tambah
              </button>
            </div>
            {(currentHouse.activities || []).map((act, idx) => (
              <div key={idx} className="input-with-action" style={{ marginBottom: '6px' }}>
                <input
                  type="text"
                  value={act}
                  onChange={e => handleListChange('activities', idx, e.target.value)}
                />
                <button type="button" className="btn-item-delete" onClick={() => handleRemoveListItem('activities', idx)}>
                  <Trash2 size={12} />
                </button>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
